const STORAGE_KEY = "lastStorefrontByOrgId";
const STORAGE_KEY_CURRENCY = "lastStorefrontCurrencyByOrgId";

type OrgToStorefrontMap = Record<string, string>;
type OrgCurrencyMap = Record<string, "CAD" | "USD">;

function readMap(): OrgToStorefrontMap {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return {};
    return parsed as OrgToStorefrontMap;
  } catch {
    return {};
  }
}

function writeMap(map: OrgToStorefrontMap) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function readCurrencyMap(): OrgCurrencyMap {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_CURRENCY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return {};
    return parsed as OrgCurrencyMap;
  } catch {
    return {};
  }
}

function writeCurrencyMap(map: OrgCurrencyMap) {
  sessionStorage.setItem(STORAGE_KEY_CURRENCY, JSON.stringify(map));
}

/** Remember which connected storefront (live or test) the user last opened for this org. */
export function rememberLastStorefrontForOrg(
  organizationId: string,
  storefrontId: string,
) {
  const map = readMap();
  map[organizationId] = storefrontId;
  writeMap(map);
}

/** Remember CAD vs USD when the org uses both storefront pairs. */
export function rememberLastStorefrontCurrencyForOrg(
  organizationId: string,
  currency: "CAD" | "USD",
) {
  const map = readCurrencyMap();
  map[organizationId] = currency;
  writeCurrencyMap(map);
}

export function getLastStorefrontCurrencyForOrg(
  organizationId: string,
): "CAD" | "USD" | undefined {
  const v = readCurrencyMap()[organizationId];
  if (v === "CAD" || v === "USD") return v;
  return undefined;
}

function normId(v: unknown): string | undefined {
  if (v == null || v === "") return undefined;
  return String(v);
}

/** GraphQL enum values; optional so utils stay decoupled from gql/generated. */
type StorefrontMode = "CAD" | "USD" | "BOTH" | null | undefined;

/**
 * Pick which storefront ID to use for deep links when an org may have up to four connected IDs
 * (CAD live/test + USD live/test). Respects `storefrontMode` so USD-only orgs are not sent to
 * legacy CAD connections. In BOTH mode, prefers the user's last CAD/USD choice when valid.
 * If the user last opened one of the allowed IDs in this session, use that; otherwise the first
 * in the mode-specific order.
 */
export function getPreferredConnectedStorefrontId(
  organizationId: string,
  liveCad: unknown,
  testCad: unknown,
  liveUsd?: unknown,
  testUsd?: unknown,
  storefrontMode?: StorefrontMode,
): string | undefined {
  const cadLive = normId(liveCad);
  const cadTest = normId(testCad);
  const usdLive = normId(liveUsd);
  const usdTest = normId(testUsd);

  let order: string[];
  if (storefrontMode === "USD") {
    order = [usdLive, usdTest].filter(Boolean) as string[];
  } else if (storefrontMode === "BOTH") {
    const currency = getLastStorefrontCurrencyForOrg(organizationId);
    if (currency === "USD") {
      order = [usdLive, usdTest, cadLive, cadTest].filter(Boolean) as string[];
    } else {
      order = [cadLive, cadTest, usdLive, usdTest].filter(Boolean) as string[];
    }
  } else {
    // CAD or missing/legacy: same priority as before multi-currency (CAD first).
    order = [cadLive, cadTest, usdLive, usdTest].filter(Boolean) as string[];
  }

  if (order.length === 0) return undefined;
  const last = readMap()[organizationId];
  if (last && order.includes(last)) return last;
  return order[0];
}

const pairKey = (liveStorefrontId: string) => `sf:${liveStorefrontId}`;

/** Remember which side (live or sandbox) of a non-org storefront pair was last opened. */
export function rememberLastStorefrontForPair(
  liveStorefrontId: string,
  storefrontId: string,
) {
  const map = readMap();
  map[pairKey(liveStorefrontId)] = storefrontId;
  writeMap(map);
}

/**
 * Where a non-org pair's Home card should open: the side last opened this
 * session, otherwise the sandbox until the live side is launched, then live.
 */
export function getPreferredStorefrontIdForPair(
  liveStorefrontId: string,
  testStorefrontId: string,
  liveIsLaunched: boolean,
): string {
  const last = readMap()[pairKey(liveStorefrontId)];
  if (last === liveStorefrontId || last === testStorefrontId) return last;
  return liveIsLaunched ? liveStorefrontId : testStorefrontId;
}

/** Prefer within a single pair (e.g. when switching CAD/USD in the Storefront app). */
export function getPreferredStorefrontIdInPair(
  organizationId: string,
  liveId: unknown,
  testId: unknown,
): string | undefined {
  const live = normId(liveId);
  const test = normId(testId);
  if (!live && !test) return undefined;
  if (live && !test) return live;
  if (!live && test) return test;
  const last = readMap()[organizationId];
  if (last && (last === live || last === test)) return last;
  return live;
}
